import { BookingInspection } from '@components/Booking/BookingInspection';
import { BSlugContactCard } from '@components/Booking/BSlugContactCard';
import { BSlugDetails } from '@components/Booking/BSlugDetails';
import { BSlugStatusUpdates } from '@components/Booking/BSlugStatusUpdates';
import { BSlugSupportCard } from '@components/Booking/BSlugSupportCard';
import { RelevantQuestionsForBooking } from '@components/Booking/RelevantQuestionsForBooking';
import { ExistingBookingSpikedTireSeasonBanner } from '@components/BookingFlow/Common/MobileBookingSpikedTireSeasonBanner';
import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { BookingLocationType, BookingRecordDetailsCustomer, BookingUpdateOfferStatus } from '@navio/nsdk';
import { noddiAsync, URLKeys } from 'noddi-async';
import { NoddiBadge, NoddiCard, NoddiTabs } from 'noddi-ui-web';
import {
  BookingDetailsTabs,
  getIsAwaitingCustomerPayment,
  getIsTireServiceJob,
  getIsUpcomingBooking
} from 'noddi-util';
import { RefObject } from 'react';
import { useProfileAddressGate } from '../../hooks/useProfileAddressGate';
import { getTabsTranslations } from '../../utils/commonTranslations';
import { isStationaryUnpaidNeedingAddress } from '../../utils/stationaryAddressGate';
import { DraftPayBeforeContent } from './DraftPayBeforeContent';

interface Props {
  booking: BookingRecordDetailsCustomer;
  defaultTab?: BookingDetailsTabs;
  paymentButtonRef?: RefObject<HTMLDivElement | null>;
}

export const BSlugContent = ({ booking, defaultTab = BookingDetailsTabs.STATUS_UPDATES, paymentButtonRef }: Props) => {
  const {
    data: offer,
    isPending: isOfferPending,
    error: _offerError
  } = noddiAsync.useGet({
    type: URLKeys.getBookingActiveBookingUpdateOffer,
    input: { slug: booking.slug }
  });

  const hasActiveOffer = offer != null && !offer.expired && offer.status === BookingUpdateOfferStatus.PENDING;

  const isBookingUpcoming = getIsUpcomingBooking(booking);
  const isStationary = booking.locationType === BookingLocationType.STATIONARY;
  // Both awaiting-payment states land on the same page: the customer's own
  // abandoned checkout and a booking staff created for them.
  const isAwaitingPayment = getIsAwaitingCustomerPayment(booking.status.value);

  // Page owns the address card; pay buttons prompt for whatever is still missing on click.
  const addressRequired = isStationaryUnpaidNeedingAddress(booking);
  const { addressSection } = useProfileAddressGate({
    userGroupId: booking.userGroup.id,
    required: addressRequired,
    countryCode: booking.address?.countryCode
  });

  if (isAwaitingPayment) {
    return (
      <div className='flex flex-col gap-4'>
        <ExistingBookingSpikedTireSeasonBanner booking={booking} />
        {addressSection}
        <DraftPayBeforeContent booking={booking} paymentButtonRef={paymentButtonRef} />
      </div>
    );
  }

  return (
    <div className='flex flex-col gap-5'>
      <ExistingBookingSpikedTireSeasonBanner booking={booking} />
      {addressSection}
      {/* Visit logistics (car alarm, tires by the car, arrival SMS): nothing of it applies without a customer visit. */}
      {isBookingUpcoming && !isStationary && booking.hasCustomerVisit && !isOfferPending && !hasActiveOffer && (
        <RelevantQuestionsForBooking booking={booking} />
      )}
      <BSlugContactCard booking={booking} />

      {/* Each tab owns its surfaces; the tab bar stays outside the section cards. */}
      {isStationary ? (
        <BSlugDetails booking={booking} paymentButtonRef={paymentButtonRef} />
      ) : (
        <NoddiTabs<BookingDetailsTabs>
          translations={getTabsTranslations()}
          tabItems={getTabItems(booking, paymentButtonRef)}
          defaultValue={defaultTab}
        />
      )}
    </div>
  );
};

function getTabItems(booking: BookingRecordDetailsCustomer, paymentButtonRef?: RefObject<HTMLDivElement | null>) {
  const isWheelChangeJob = getIsTireServiceJob(booking);
  const hasNewResults = booking.completedAt && !booking.order.isFullyPaid;

  return [
    {
      label: t`Updates`,
      value: BookingDetailsTabs.STATUS_UPDATES,
      node: (
        <div className='flex flex-col gap-4'>
          <NoddiCard>
            <BSlugStatusUpdates booking={booking} paymentButtonRef={paymentButtonRef} />
          </NoddiCard>
          <BSlugSupportCard supportEmail={booking.supportEmail} supportPhone={booking.supportPhone} />
        </div>
      )
    },
    {
      label: t`Booking`,
      value: BookingDetailsTabs.ORDER_DETAILS,
      node: (
        <div className='flex flex-col gap-4'>
          <BSlugDetails booking={booking} paymentButtonRef={paymentButtonRef} />
          <BSlugSupportCard supportEmail={booking.supportEmail} supportPhone={booking.supportPhone} />
        </div>
      )
    },
    ...(isWheelChangeJob
      ? [
          {
            label: t`Inspection`,
            value: BookingDetailsTabs.COMPLETED_BOOKING,
            node: <BookingInspection booking={booking} />,
            badge: hasNewResults ? (
              <NoddiBadge
                variant='success'
                className='h-5 border-emerald-500 bg-emerald-500 px-1.5 py-0.5 text-xs text-white'
              >
                <Trans>New</Trans>
              </NoddiBadge>
            ) : undefined
          }
        ]
      : [])
  ];
}
